import React from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import LinkTag from '@/components/common/LinkTag';
import ListMagazineLine from '@/components/common/communication/magazine/ListMagazineLine';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import MagazineSkeleton from '@/components/templates/mobile/communication/Magazine/MagazineMobileTemplate/MagazineSkeleton';
import useFaqTagViewModel from '@/hooks/faq/useFaqTagViewModel';
import { Tablet } from '@/styles/layout';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

interface Props extends CustomPageProps {
  tag: string;
}

const FaqTagListTemplate = ({ tag, client }: Props) => {
  const viewModel = useFaqTagViewModel(tag);

  if (viewModel.faqsLoading) {
    return <MagazineSkeleton />;
  }

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title='1분 FAQ' />
        </TemplateLayout.Header>
      )}
      <Wrapper>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbsWrapper>
            <BreadCrumbs visible path={`${ROUTES.FAQ.TAG.path}`} type='magazine' />
          </BreadCrumbsWrapper>
        )}
        <Content>
          <PageTile>{`#${tag}`}</PageTile>
          <ul>
            {viewModel.faqs.map((faq) => (
              <LinkTag
                key={faq.magazinePid}
                href={`${ROUTES.FAQ.DETAIL.path}/${faq.magazinePid}`}
                onClickNavigateToScreen={() => viewModel.onClickMove(faq.magazinePid)}
              >
                <ListMagazineLine
                  magazine={{
                    title: faq.title,
                    mobileTitle: faq.mobileTitle,
                    image: '',
                    date: faq.date,
                    magazinePid: faq.magazinePid,
                    description: faq.description,
                    mobileDescription: faq.mobileDescription,
                    siteName: '',
                  }}
                />
              </LinkTag>
            ))}
          </ul>
        </Content>
      </Wrapper>
    </TemplateLayout.Wrapper>
  );
};

export default FaqTagListTemplate;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }) => theme.color.white};
`;

export const BreadCrumbsWrapper = styled(BreadCrumbs.Wrapper)`
  width: 100%;
  padding-bottom: 16px;
  background-color: ${({ theme }) => theme.color.white};
`;

const Content = styled.main`
  display: flex;
  width: 100%;
  max-width: 1152px;
  flex-direction: column;
`;

const PageTile = styled.h1`
  padding: 16px 24px 8px 24px;
  color: ${theme.color.gray700};
  ${theme.typography.UI28SB};

  @media screen and (max-width: ${Tablet}) {
    ${theme.typography.UI22SB};
  }
`;
